//
// CaseChat - Chat interface with message virtualization, audio recording,
// and questionnaire functionality
//
// Must be wrapped in a provider/context which gives:
//
// chat:                  // All elements to handle this given chat
//    utils:              // Functions to handle this type of chats
//    discussion:         // object containing all info identifying this chat
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  AutoSizer,
  CellMeasurerCache,
  List,
} from 'react-virtualized';
import 'react-virtualized/styles.css';

import {
  CheckList2,
  Mic,
  Send,
  Square,
} from '../../assets/js/svg/gui';
import useCaseRefreshOnPush from '../../stores/push/useCaseRefreshOnPush';
import usePush from '../../stores/push/usePush';
import useExplainAction from '../../stores/tutorial/useExplainAction';
import {
  appTooltipProps,
  logColor,
  useIntervalAtMost,
} from '../../utils';
import { CaseButton } from '../buttons';
import {
  Column,
  Row,
} from '../display';
import { TextInput } from '../input';
import SendPicturePopup from '../media/SendPicturePopup';
import { useIsMounted } from '../React';

import ChatAudioRecorder from './chat/ChatAudioRecorder';
import {
  chatComposeValid,
  chatQuestionAddValid,
  chatStateReducer,
  initialChatState,
  maxMessageFetchCnt,
  releaseFetchHold,
  temporaryScrollLockTo,
} from './chat/utils';

const defaultMedia = {
  ready: false,
};

// Blank space kept above the very first message. The "public messages" warning
// floats pinned to the top of the scroll area, so without this it would sit on
// top of the first message and hide it.
const firstMessageTopSpacer = 44;

// Cap voice-recording length: at the recorder's 32 kbps, 5 min ≈ 1.2 MB, safely under the media upload limit
const audioMaxDurationMs = 5 * 60 * 1000;

function CaseChat(props) {
  const explainAction = useExplainAction();
  const {
    translate,
    children = null,
    className,
    style = undefined,
    chat,
    renderMessage,
    enable = true,
    Warning,
    withAudio,
    media = defaultMedia,
    QuestionnaireSelection,
    createAlert,
    composeDisabled,
    controlsWithChildren,
    sendBadge = null,
    composeLegend = null,
    // topBottomShades,
    alwaysGetAllMessages,
    CustomButton,
    composeBackgroundColor,
    composeFrameColor,
    // Read-only viewer (supervisor who has not joined): messages load even
    // without a participant id, but the composer is hidden so they cannot send.
    readOnly = false,
    // Viewer entitled to send before joining (caseAutoJoin): the send itself
    // puts them on the case, so the composer works without a participant id.
    composeBeforeJoining = false,
  } = props;
  const {
    discussion,
    myId,
    getInfo,
    fetchMessages,
    sendMessage,
    sendPicture,
    sendQuestion,
    sendAudio,
  } = chat;
  const {
    audioAvailable,
    audioRecordingAvailable,
    audioStartContext,
    audioContextRef,
    audioAnalyzerRef,
    getMedia,
  } = media;
  // Reading needs standing on the case, composing needs standing plus the right
  // to write; a participant id is only one of the ways to hold either
  const mayRead = Boolean(myId) || readOnly || composeBeforeJoining;
  const mayCompose = Boolean(discussion) && (Boolean(myId) || composeBeforeJoining);

  const messagesRef = useRef(null);
  const participantsRef = useRef({});
  const messageListRef = useRef(null);
  const [state, dispatch] = useReducer(chatStateReducer, initialChatState);
  const [audioBlob, setAudioBlob] = useState();
  const [audioDuration, setAudioDuration] = useState();
  const isMountedRef = useIsMounted();
  // discussion is the csid (see TownCaseChatProvider). Used to scope chat
  // notifications to this case.
  const { push } = usePush(false);
  const wsConnected = push?.wsConnected;

  const cache = useMemo(
    () => {
      //
      // Force cache to be recreated when chat goes
      // from disabled to enabled
      if (enable) {
        const newCache = new CellMeasurerCache({
          fixedWidth: true,
          defaultHeight: 68,
        });
        return (newCache);
      }

      return undefined;
    },
    [enable],
  );

  useEffect(
    () => {
      if (messageListRef.current) messageListRef.current.forceUpdate();
    },
    [renderMessage],
  );

  const getInfoCallback = useCallback(
    (error, info) => {
      if ((!error) && info && isMountedRef.current && cache) {
        // Force msg rendering update when users are updated
        if (info.participantsChanged) messageListRef.current?.forceUpdateGrid();
        dispatch({
          type: 'info',
          ...info,
        });
      }
    },
    [isMountedRef, cache],
  );

  const fetchMessagesCallback = useCallback(
    (holdScroll) => (error, info) => {
      releaseFetchHold(dispatch, isMountedRef);
      if ((!error) && info && isMountedRef.current) {
        const { lengthLoaded, ...otherInfo } = info;

        if (holdScroll && lengthLoaded) {
          if (holdScroll === 'first') {
            temporaryScrollLockTo(dispatch, isMountedRef);
          } else if (holdScroll === 'last') {
            temporaryScrollLockTo(dispatch, isMountedRef);
          } else {
            logColor(`Error!!! fetchMessages holdScroll="${holdScroll}"`);
          }
        }

        dispatch({
          type: 'load',
          ...otherInfo,
        });
      }
    },
    [isMountedRef],
  );

  const sendMessageCallback = useCallback(
    (error, info) => {
      if ((!error) && info && isMountedRef.current) {
        dispatch({
          type: 'sent',
          ...info,
        });
      }
    },
    [isMountedRef],
  );

  //
  // Init
  //
  useEffect(
    () => {
      dispatch({
        type: 'reset',
        enable,
        discussion,
      });
      messagesRef.current = [];
      participantsRef.current = {};
    },
    // !!!!
    //  Omit myId: chatStateReducer's 'reset' case no-ops for an unchanged
    // discussion, so this ref wipe must not re-run on myId changes alone or
    // the refs desync from rowCount/maxIdLoaded.
    [enable, discussion, cache],
  );

  useEffect(
    () => {
      if (discussion && mayRead && enable) {
        getInfo(participantsRef, getInfoCallback, true)();
      }
    },
    [enable, discussion, mayRead, getInfo, getInfoCallback],
  );

  const getInfoMemoized = useCallback(
    (callback) => {
      getInfo(participantsRef, getInfoCallback, false)(callback);
    },
    [getInfo, getInfoCallback],
  );

  // Polling is the fallback: only while the WS channel is down. While
  // connected, new messages arrive via this case's notifications below
  // (getInfo refreshes msgCnt, which the message-fetch effect picks up).
  useIntervalAtMost(
    getInfoMemoized,
    (enable && discussion && state.ready && (!wsConnected)) ? 3000 : undefined,
  );

  const onChatEvent = useCallback(
    () => { getInfoMemoized(() => {}); },
    [getInfoMemoized],
  );
  const chatFilter = useMemo(
    () => ((discussion) ? { csid: discussion } : undefined),
    [discussion],
  );
  useCaseRefreshOnPush(
    onChatEvent,
    { enabled: (enable && Boolean(discussion) && state.ready), filter: chatFilter },
  );

  // On WS (re)connect, reconcile any messages missed while the channel was down.
  const wasWsConnectedRef = useRef(false);
  useEffect(
    () => {
      if (enable && discussion && state.ready && wsConnected && (!wasWsConnectedRef.current)) {
        getInfoMemoized(() => {});
      }
      wasWsConnectedRef.current = wsConnected;
    },
    [wsConnected, enable, discussion, state.ready, getInfoMemoized],
  );

  //
  // Get initial messages, and append to list when new ones come in
  //
  useEffect(
    () => {
      if (enable
          && discussion
          && mayRead
          && state.msgCnt
          && !state.fetchHoldCnt) {
        let since;
        const upTo = state.msgCnt;
        let holdScroll;
        if (state.maxIdLoaded === null) {
        // Initial load
        // console.log('Get initial messages');

          if (alwaysGetAllMessages) {
            since = 1;
          } else {
            since = (state.msgCnt > maxMessageFetchCnt)
              ? (state.msgCnt - maxMessageFetchCnt + 1)
              : 1;
          }
          holdScroll = 'last';
        } else {
          // Append messages
          since = state.maxIdLoaded + 1;
          // Hold scrolling and fetching if we want to stick to the last message
          holdScroll = (state.stickToLastMessage) ? 'last' : undefined;
        }

        if ((since !== undefined) && (upTo >= since)) {
          dispatch({ type: 'fetchHold' });
          fetchMessages(
            since,
            upTo,
            messagesRef,
            fetchMessagesCallback(holdScroll),
          );
        }
      }
    },
    [
      enable, discussion, mayRead, fetchMessages, alwaysGetAllMessages,
      state.msgCnt, state.maxIdLoaded,
      state.fetchHoldCnt, state.stickToLastMessage,
      fetchMessagesCallback,
    ],
  );

  //
  // Get earlier messages on scroll up
  //
  useEffect(
    () => {
      if (enable && discussion
      && mayRead
      && (state.minIdLoaded !== null)
      && (state.overscanStartIndex !== null)
      && ((state.overscanStartIndex + 1) < state.minIdLoaded)
      && !state.fetchHoldCnt) {
        const since = (state.minIdLoaded > maxMessageFetchCnt)
          ? state.minIdLoaded - maxMessageFetchCnt
          : 1;
        const upTo = state.minIdLoaded - 1;

        if ((since !== undefined) && (upTo >= since)) {
          dispatch({ type: 'fetchHold' });
          fetchMessages(
            since,
            upTo,
            messagesRef,
            // Ask to hold further refresh for a while, and recitify scrolling:
            fetchMessagesCallback('first'),
          );
        }
      }
    },
    [
      enable, discussion, mayRead, fetchMessages,
      state.fetchHoldCnt, state.minIdLoaded,
      state.overscanStartIndex,
      fetchMessagesCallback,
    ],
  );

  //
  // When new messages get added, recompute heights -> updates the display
  // If needed, keep sticking to last message
  useEffect(
    () => {
      if (state.minIdLoaded !== null) {
        if ((state.minIdComputed !== null)
            && (state.minIdLoaded < state.minIdComputed)) {
          // Handle beginning of list inserts
          // console.log(`state.minIdLoaded=${state.minIdLoaded}, state.minIdComputed=${
          //   state.minIdComputed}`);

          // console.log(`Loaded earlier messages, recomputeRowHeights(${state.minIdLoaded - 1})`);
          for (let index = state.minIdLoaded - 1; index < state.minIdComputed; index += 1) {
            cache.clear(index, 0);
          }
          messageListRef.current.recomputeRowHeights(state.minIdLoaded - 1);
          messageListRef.current.forceUpdateGrid();

          dispatch({
            type: 'renderedUpTo',
            newMinIdComputed: state.minIdLoaded,
          });
        }

        if ((state.maxIdComputed === null) && (state.minIdLoaded !== null)) {
          // Handle init
          // console.log(`Init msg render, from ${state.minIdLoaded - 1} to ${
          //   state.maxIdLoaded - 1}`);

          cache.clearAll();
          for (let index = state.minIdLoaded - 1; index < state.maxIdLoaded; index += 1) {
            cache.clear(index, 0);
          }
          messageListRef.current.recomputeRowHeights(state.minIdLoaded - 1);
          messageListRef.current.scrollToRow(state.maxIdLoaded - 1);
          dispatch({
            type: 'renderedUpTo',
            newMinIdComputed: state.minIdLoaded,
            newMaxIdComputed: state.maxIdLoaded,
          });
        } else if ((state.maxIdLoaded > state.maxIdComputed)
                  && (state.maxIdComputed !== null)) {
          // Handle end of list updates

          messageListRef.current.recomputeRowHeights(state.maxIdComputed);
          dispatch({
            type: 'renderedUpTo',
            newMaxIdComputed: state.maxIdLoaded,
          });
        }
      }
    },
    [
      state.minIdLoaded, state.minIdComputed,
      cache,
      state.maxIdComputed, state.maxIdLoaded,
    ],
  );

  //
  // If needed, keep sticking to last message when new messages get added
  //
  useEffect(
    () => {
      if ((state.stickToLastMessage)
        && (state.maxIdLoaded !== null)
        && (state.stopIndex !== null)
        && ((state.maxIdLoaded - 1) !== state.stopIndex)) {
        // Next frame: re-measured rows move stopIndex, a synchronous re-scroll loops past React's update depth
        const frame = requestAnimationFrame(() => {
          messageListRef.current?.scrollToRow(state.maxIdLoaded - 1);
        });
        return () => cancelAnimationFrame(frame);
      }
      return undefined;
    },
    [
      state.stickToLastMessage,
      state.maxIdLoaded, state.stopIndex,
    ],
  );

  const handleComposeChange = useCallback(
    (event) => {
      if (event.target) {
        dispatch({
          type: 'compose',
          discussion,
          value: event.target.value,
        });
      }
    },
    [discussion],
  );

  const handleComposeQuestionChange = useCallback(
    (event) => {
      if (event.target?.value) {
        dispatch({
          type: 'composeQuestion',
          discussion,
          value: event.target.value,
        });
      }
    },
    [discussion],
  );

  const handleComposeKeyPress = useCallback(
    (event) => {
      if ((event.key === 'Enter')
          && (event.ctrlKey === false)
          && (event.shiftKey === false)) {
        //
        // Simple 'return'
        //
        if (chatComposeValid(state.compose)
              && (!state.audioPending)
              && (!state.questionPending)) {
          event.preventDefault();
          const message = state.compose;
          if (state.stickToLastMessage) {
            temporaryScrollLockTo(dispatch, isMountedRef);
          }
          sendMessage(message, sendMessageCallback);
          dispatch({ type: 'clearCompose' });
        }

        if (chatQuestionAddValid(state.questionAdd)
            && (!state.audioPending)
            && state.questionPending) {
          //
          // Questionnaire
          if (state.stickToLastMessage) {
            temporaryScrollLockTo(dispatch, isMountedRef);
          }
          sendQuestion(
            state.questionId,
            state.questionAdd,
            sendMessageCallback,
          );
          return dispatch({ type: 'clearCompose' });
        }
      }

      return undefined;
    },
    [
      sendMessage, sendQuestion, sendMessageCallback, state.compose,
      state.stickToLastMessage, state.audioPending, state.questionPending,
      state.questionId, state.questionAdd, isMountedRef,
    ],
  );

  const pictureInputRef = useRef();
  const [pictureFile, setPictureFile] = useState(null);
  const [pictureSending, setPictureSending] = useState(false);

  const handlePictureClick = useCallback(
    () => {
      // Re-picking the same file has to fire onChange again
      if (pictureInputRef.current) pictureInputRef.current.value = '';
      pictureInputRef.current?.click();
    },
    [],
  );

  // Picking only opens the confirmation: the picture leaves on its own send
  const handlePictureInput = useCallback(
    (event) => {
      setPictureFile(event.target.files?.[0] ?? null);
    },
    [],
  );

  const sendPickedPicture = useCallback(
    async ({ file, rotation, note }) => {
      setPictureFile(null);
      setPictureSending(true);
      await sendPicture(file, { rotation, note });
      setPictureSending(false);
      // The message count is what the fetch effect watches
      getInfoMemoized(() => {});
    },
    [sendPicture, getInfoMemoized],
  );

  const handleSendClick = useCallback(
    () => {
      if (chatComposeValid(state.compose)
          && (!state.audioPending)
          && (!state.questionPending)) {
        //
        // Regular message
        if (state.stickToLastMessage) {
          temporaryScrollLockTo(dispatch, isMountedRef);
        }
        sendMessage(
          state.compose,
          sendMessageCallback,
        );
        return dispatch({ type: 'clearCompose' });
      }

      if (chatQuestionAddValid(state.questionAdd)
          && (!state.audioPending)
          && state.questionPending) {
        //
        // Questionnaire
        if (state.stickToLastMessage) {
          temporaryScrollLockTo(dispatch, isMountedRef);
        }
        sendQuestion(
          state.questionId,
          state.questionAdd,
          sendMessageCallback,
        );
        return dispatch({ type: 'clearCompose' });
      }

      if (state.audioPending && audioDuration) {
        //
        // Audio message
        if (state.stickToLastMessage) {
          temporaryScrollLockTo(dispatch, isMountedRef);
        }
        sendAudio(
          audioBlob,
          audioDuration,
          sendMessageCallback,
        );
        setAudioBlob();
        setAudioDuration();
      }

      return undefined;
    },
    [
      sendMessage, sendQuestion, sendAudio,
      sendMessageCallback, state.compose,
      state.stickToLastMessage, state.audioPending, state.questionPending,
      state.questionId, state.questionAdd,
      audioBlob, audioDuration, isMountedRef,
    ],
  );

  //
  // Observe scroll events, due to user, or to messages list changing
  //
  const handleOnScroll = useCallback(
    (event) => {
      if (messageListRef.current) {
        dispatch({
          type: 'scroll',
          ...event,
        });
      }
    },
    [],
  );

  const handleOnRowsRendered = useCallback(
    (event) => {
      if (messageListRef.current) {
        dispatch({
          type: 'rendered',
          ...event,
        });
      }
    },
    [],
  );

  const handleAudioMouse = useCallback(
    (event) => {
      if (audioRecordingAvailable) {
        audioStartContext();
        dispatch({ type: 'audioMouse', mouseEvent: event.type });
      }
    },
    [audioRecordingAvailable, audioStartContext],
  );

  useEffect(
    () => {
      if (state.recordingAudio) {
        const mouseTimeout = setTimeout(
          () => { dispatch({ type: 'audioHoldTimeout' }); },
          200,
        );
        return () => {
          clearTimeout(mouseTimeout);
        };
      }
      return undefined;
    },
    [state.recordingAudio],
  );

  //
  // Auto-stop a recording that reaches the max length, keeping the clip for review/send
  //
  useEffect(
    () => {
      if (state.recordingAudio) {
        const maxDurationTimeout = setTimeout(
          () => { dispatch({ type: 'audioMaxDuration' }); },
          audioMaxDurationMs,
        );
        return () => {
          clearTimeout(maxDurationTimeout);
        };
      }
      return undefined;
    },
    [state.recordingAudio],
  );

  const handleQuestionClick = useCallback(
    () => {
      dispatch({ type: 'openQuestion' });
    },
    [],
  );

  const handleTrashClick = useCallback(
    () => {
      dispatch({ type: 'cancelAudioQuestion' });
      setAudioBlob();
      setAudioDuration();
    },
    [],
  );

  const setQuestion = useCallback(
    (questionId, addTextEnable) => {
      dispatch({
        type: 'setQuestion',
        questionId,
        addTextEnable,
        text: translate(questionId, 'questionnairePrompts'),
      });
    },
    [translate],
  );

  const setCompose = useCallback(
    (text) => {
      dispatch({
        type: 'compose',
        discussion,
        value: text,
      });
      dispatch({ type: 'cancelAudioQuestion' });
    },
    [discussion],
  );

  const composeField = useMemo(
    () => {
      if (state.audioPending) {
        const cancelAudio = () => {
          dispatch({ type: 'cancelAudioQuestion' });
          setAudioBlob();
          setAudioDuration();
        };

        return (
          <ChatAudioRecorder
            translate={translate}
            recordingAudio={state.recordingAudio}
            audioAvailable={audioAvailable}
            audioStartContext={audioStartContext}
            audioContextRef={audioContextRef}
            audioAnalyzerRef={audioAnalyzerRef}
            audioBlob={audioBlob}
            setAudioBlob={setAudioBlob}
            audioDuration={audioDuration}
            setAudioDuration={setAudioDuration}
            CustomButton={CustomButton}
            composeBackgroundColor={composeBackgroundColor}
            composeFrameColor={composeFrameColor}
            cancelAudio={cancelAudio}
          />
        );
      }

      if (state.questionPending) {
        return (
          <TextInput
            className="flex-1 overflow-auto bg-transparent border-none"
            id="compose"
            multiline
            onChange={handleComposeQuestionChange}
            onKeyPress={handleComposeKeyPress}
            value={state.questionCompose}
            hideLabel
          />
        );
      }

      return (
        <TextInput
          className="flex-1 overflow-auto bg-transparent border-none"
          id="compose"
          placeholder={`${translate('newMessage', 'chat')}...`}
          multiline
          onChange={handleComposeChange}
          onKeyPress={handleComposeKeyPress}
          value={state.compose}
          hideLabel
        />
      );
    },
    [
      audioAnalyzerRef, audioBlob, audioContextRef, audioDuration,
      handleComposeChange, handleComposeKeyPress, handleComposeQuestionChange,
      state.audioPending, state.compose, state.questionCompose, state.questionPending,
      state.recordingAudio, translate,
      CustomButton, composeBackgroundColor, composeFrameColor,
      audioAvailable, audioStartContext,
    ],
  );

  const composeControls = useMemo(
    () => (
      <>
        {(sendPicture)
          ? (
              <span {...appTooltipProps(translate('sendPictureDescr', 'chat'))}>
                <input
                  ref={pictureInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePictureInput}
                />
                <CaseButton
                  imgSrc="/images/svg/gui/camera.svg"
                  disabled={(!mayCompose)
                    || pictureSending
                    || state.questionPending
                    || composeDisabled}
                  onClick={handlePictureClick}
                  noFrame
                  imgColor="var(--color-pxPrimary)"
                  imgClassname="p-0.5"
                />
              </span>
            )
          : null}

        {(withAudio)
          ? (
              <span
                id="tutorialAudioRecorder"
                {...appTooltipProps(translate('audioRecDescr', 'chat'))}
              >
                {
                  (CustomButton)
                    ? (
                        <CustomButton
                          className={classNames(
                            'h-9 w-9 rounded-full p-1.5 ml-3 shadow-button',
                            {
                              'animate-[redShadowGlow_1s_ease-in-out_infinite]': state.recordingAudio,
                            },
                          )}
                          onMouseDown={explainAction('home.cases.audio', handleAudioMouse, { consumeFirst: true })}
                          onMouseUp={handleAudioMouse}
                          Icon={(state.recordingAudio)
                            ? (
                                <Square className={classNames(
                                  'w-full h-full',
                                  {
                                    'fill-pxUiAlert animate-[saturateGlow_1s_ease-in-out_infinite]': state.recordingAudio,
                                    'fill-pxAccentBlue': !state.recordingAudio,
                                  },
                                )}
                                />
                              )
                            : <Mic className="w-full h-full fill-pxAccentBlue" />}
                          outsideColor={composeFrameColor}
                          outsideNeuMorphismColor={composeFrameColor}
                          disabled={
                            (!(mayCompose && audioRecordingAvailable))
                        || state.questionPending
                        || composeDisabled
                          }
                        />
                      )
                    : (
                        <CaseButton
                          imgSrc={(state.recordingAudio) ? '/images/svg/gui/square.svg' : '/images/svg/gui/mic.svg'}
                          disabled={
                            (!(mayCompose && audioRecordingAvailable))
                        || state.questionPending
                        || composeDisabled
                          }
                          onMouseDown={explainAction('home.cases.audio', handleAudioMouse, { consumeFirst: true })}
                          onMouseUp={handleAudioMouse}
                          imgClassname={classNames(
                            'p-0.5',
                            {
                              'fill-pxUiAlert animate-[saturateGlow_1s_ease-in-out_infinite]': state.recordingAudio,
                            },
                          )}
                          noFrame
                          imgColor="var(--color-pxPrimary)"
                        />
                      )
                }
              </span>
            )
          : null}

        <span
          {...appTooltipProps(translate('sendDescr', 'chat'))}
          className="relative"
        >
          {
            (CustomButton)
              ? (
                  <CustomButton
                    className="h-9 w-9 rounded-full p-1.5 ml-3 shadow-button"
                    onClick={handleSendClick}
                    Icon={<Send className="w-full h-full fill-pxAccentBlue" />}
                    outsideColor={composeFrameColor}
                    outsideNeuMorphismColor={composeFrameColor}
                    disabled={
                      (!mayCompose)
                    || ((!state.audioPending)
                        && (!state.questionPending)
                        && (!chatComposeValid(state.compose)))
                    || (state.audioPending
                        && (!audioDuration))
                    || (state.questionPending
                        && (!state.questionId)
                        && (!chatQuestionAddValid(state.questionAdd)))
                    }
                  />
                )
              : (
                  <CaseButton
                    onClick={handleSendClick}
                    imgSrc="/images/svg/gui/send.svg"
                    disabled={
                      (!mayCompose)
                    || ((!state.audioPending)
                        && (!state.questionPending)
                        && (!chatComposeValid(state.compose)))
                    || (state.audioPending
                        && (!audioDuration))
                    || (state.questionPending
                        && (!state.questionId)
                        && (!chatQuestionAddValid(state.questionAdd)))
                    }
                  />
                )
          }
          {(sendBadge)
            ? (
                <span className="absolute -top-1 -right-1 pointer-events-none">
                  {sendBadge}
                </span>
              )
            : null}

        </span>
      </>
    ),
    [
      audioDuration, handleAudioMouse, handleSendClick, explainAction,
      audioRecordingAvailable, mayCompose, state.audioPending, state.compose, state.questionAdd,
      state.questionId, state.questionPending, state.recordingAudio, translate,
      withAudio, CustomButton, composeFrameColor, composeDisabled,
      sendPicture, handlePictureClick, handlePictureInput, pictureSending,
      sendBadge,
    ],
  );

  const composeAudioControls = useMemo(
    () => (
      <>
        {( // state.audioPending ||
          state.questionPending)
          ? (
              <span
                {...appTooltipProps(translate('cancel'))}
              >
                <CaseButton
                  imgSrc="/images/svg/gui/trashOpen.svg"
                  onClick={handleTrashClick}
                />
              </span>
            )
          : null}

        {(QuestionnaireSelection)
          ? (
              <span
                {...appTooltipProps(translate('questionnairesDescr', 'chat'))}
              >
                {
                  (CustomButton)
                    ? (
                        <CustomButton
                          className={classNames(
                            'h-9 w-9 rounded-full p-1.5 ml-3 shadow-button',
                            {
                              'opacity-50': (!mayCompose) || state.audioPending || state.questionPending,
                            },
                          )}
                          disabled={(!mayCompose)
                          || state.audioPending
                          || state.questionPending}
                          onClick={handleQuestionClick}
                          Icon={<CheckList2 className="w-full h-full fill-pxAccentBlue" />}
                          outsideColor={composeBackgroundColor}
                          outsideNeuMorphismColor={composeBackgroundColor}
                        />
                      )
                    : (
                        <CaseButton
                          Image={CheckList2}
                          disabled={(!mayCompose)
                                    || state.audioPending
                                    || state.questionPending}
                          onClick={handleQuestionClick}
                        />
                      )
                }
              </span>
            )
          : null}
      </>
    ),
    [
      QuestionnaireSelection, handleQuestionClick, handleTrashClick,
      mayCompose, state.audioPending, state.questionPending, translate, CustomButton,
      composeBackgroundColor,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const baseRowRenderer = useCallback(
    renderMessage(
      translate,
      cache,
      messagesRef,
      participantsRef,
      myId,
      discussion,
      getMedia,
      audioAvailable,
      audioStartContext,
      audioContextRef,
      audioAnalyzerRef,
      createAlert,
      messageListRef,
    ),
    [
      renderMessage,
      translate, cache, messagesRef, participantsRef,
      myId, discussion, getMedia,
      audioContextRef, audioAnalyzerRef, createAlert,
      messageListRef,
    ],
  );

  // Pad the first row's top so the floating public-messages warning never hides
  // it. Applied through the row style, so CellMeasurer measures it into the row
  // and it scrolls away with the list once earlier messages are above the fold.
  const rowRendererMemoized = useCallback(
    (rowProps) => ((rowProps.index === 0)
      ? baseRowRenderer({
          ...rowProps,
          style: { ...rowProps.style, paddingTop: firstMessageTopSpacer },
        })
      : baseRowRenderer(rowProps)),
    [baseRowRenderer],
  );

  return (
    <Column
      className={classNames(
        'flex-1 w-full text-base font-normal',
        className,
      )}
      style={style}
      gap={0}
      id="messagesColumn"
    >
      <div
        className="flex flex-col flex-1 w-full relative bg-pxCaseHeaderSecondaryBg z-0 px-2"
      >
        {/* <div className="w-full h-0.5 bg-pxMsgsBgnd shrink-0" /> */}

        <div
          className="flex-1 w-full relative overflow-hidden bg-pxMsgsBgnd z-0 border-solid border-0.5 border-pxCaseHeaderSecondaryBrdr rounded-xl"
          id={`messageDiv${discussion}`}
        >
          <div className="pointer-events-none absolute top-0 inset-x-0 h-2 z-[3] bg-gradient-to-b from-white to-white/0" />

          {(state.ready)
            ? (
                <AutoSizer>
                  {({ width, height }) => (
                    <List
                      ref={messageListRef}
                      width={width}
                      height={height}
                      deferredMeasurementCache={cache}
                      rowHeight={cache.rowHeight}
                      rowRenderer={rowRendererMemoized}
                      rowCount={state.rowCount}
                      onScroll={handleOnScroll}
                      onRowsRendered={handleOnRowsRendered}
                      style={{
                        zIndex: 2,
                        // Avoid messages being wrongly displayed once
                        // scrollbar is added (after rendering!)
                        overflowY: 'scroll',
                        overflowX: 'hidden',
                      }}
                    />
                  )}
                </AutoSizer>
              )
            : null}
        </div>
      </div>

      <Column id="tutorialCaseCompose" gap={0} className="w-full px-2 pb-2 bg-pxCaseHeaderSecondaryBg">
        {(controlsWithChildren)
          ? (
              <>
                <Row gap={0} between className="composeAndChildrenDiv">
                  {children}
                  <Row>
                    {composeControls}
                  </Row>
                </Row>
                <div className="hidden" />
              </>
            )
          : children}

        {(state.questionSelecting)
          ? (
              <QuestionnaireSelection
                translate={translate}
                setQuestion={setQuestion}
                setCompose={setCompose}
                cancelQuestion={handleTrashClick}
              />
            )
          : null}

        {(composeDisabled || readOnly)
          ? null
          : (
              <Column
                gap={0}
                crossStretch
                className={classNames(
                  'w-full bg-white px-2 py-1.5 rounded-b-xl border-0.5 border-solid border-pxCaseHeaderSecondaryBrdr',
                  {
                    'bg-checkers': Warning,
                  },
                )}
              >
                <Row id="tutorialChatInput" fullw gap={0.5}>
                  {composeField}

                  {composeAudioControls}
                  {(controlsWithChildren) ? null : composeControls}
                </Row>

                {composeLegend}
              </Column>
            )}
      </Column>

      {(pictureFile)
        ? (
            <SendPicturePopup
              file={pictureFile}
              onConfirm={sendPickedPicture}
              onReject={() => setPictureFile(null)}
            />
          )
        : null}
    </Column>
  );
}

CaseChat.propTypes = {
  translate: PropTypes.func.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  chat: PropTypes.shape({
    discussion: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({}),
    ]),
    getInfo: PropTypes.func.isRequired,
    fetchMessages: PropTypes.func.isRequired,
    sendMessage: PropTypes.func.isRequired,
    sendQuestion: PropTypes.func,
    sendAudio: PropTypes.func.isRequired,
    sendPicture: PropTypes.func,
    myId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }).isRequired,
  renderMessage: PropTypes.func.isRequired,
  enable: PropTypes.bool,
  Warning: PropTypes.node,
  withAudio: PropTypes.bool,
  media: PropTypes.shape({
    ready: PropTypes.bool,
    started: PropTypes.bool,
    start: PropTypes.func,
    audioAvailable: PropTypes.bool,
    audioStartContext: PropTypes.func,
    audioContextRef: PropTypes.shape({}),
    audioAnalyzerRef: PropTypes.shape({}),
    getMedia: PropTypes.func.isRequired,
  }),
  QuestionnaireSelection: PropTypes.func,
  createAlert: PropTypes.func,
  composeDisabled: PropTypes.bool,
  controlsWithChildren: PropTypes.bool,
  // Marker pinned on the send button, telling what posting the message exposes
  sendBadge: PropTypes.node,
  // Line under the compose field, in the box's own bottom padding
  composeLegend: PropTypes.node,
  // topBottomShades: PropTypes.bool,
  readOnly: PropTypes.bool,
  composeBeforeJoining: PropTypes.bool,
  alwaysGetAllMessages: PropTypes.bool,
  CustomButton: PropTypes.func,
  composeBackgroundColor: PropTypes.string,
  composeFrameColor: PropTypes.string,
};

export default CaseChat;
